import React, { useEffect, useRef, useState } from "react";
import { X as XIcon, Zap, Loader2, MessageSquare, Users, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Join modal — adapts to tournament.team_size:
 *  - team_size = 1: solo flow, captain fills their own IGN/Player ID/Discord.
 *  - team_size > 1: captain fills a roster of N players plus a team name.
 *    Captain auto-fills slot 1 from their saved profile (last_*).
 */
export default function JoinTournamentModal({ tournament, open, onClose, onSubmitted }) {
  const { user } = useAuth();
  const teamSize = Math.max(1, Math.min(5, Number(tournament?.team_size || 1)));
  const isTeamMode = teamSize > 1;

  const blankMember = () => ({ ign: "", player_id: "", discord_username: "" });
  const [teamName, setTeamName] = useState("");
  const [roster, setRoster] = useState(() => Array.from({ length: teamSize }, blankMember));
  const [logoUrl, setLogoUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const seeded = Array.from({ length: teamSize }, (_, i) => {
      if (i === 0) {
        return {
          ign: user?.last_ign || user?.username || "",
          player_id: user?.last_player_id || "",
          discord_username: user?.last_discord_username || "",
        };
      }
      return blankMember();
    });
    setRoster(seeded);
    setTeamName("");
    setLogoUrl("");
  }, [open, teamSize, user]);

  if (!open || !tournament) return null;

  const updateMember = (idx, key, value) => {
    setRoster((prev) => prev.map((m, i) => (i === idx ? { ...m, [key]: value } : m)));
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file (PNG, JPG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo too large — max 5 MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url || data.path || "");
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!logoUrl) return toast.error("Team logo is required");
    // Captain (slot 0) must always be filled.
    const captain = roster[0];
    if (!captain.ign.trim() || !captain.player_id.trim() || !captain.discord_username.trim()) {
      return toast.error("Your IGN, Player ID and Discord username are required");
    }
    if (isTeamMode) {
      if (!teamName.trim()) return toast.error("Team name is required");
      for (let i = 1; i < teamSize; i++) {
        const m = roster[i];
        if (!m.ign.trim() || !m.player_id.trim() || !m.discord_username.trim()) {
          return toast.error(`Player ${i + 1}: all fields are required`);
        }
      }
      const dh = new Set();
      for (const m of roster) {
        const k = m.discord_username.trim().toLowerCase();
        if (dh.has(k)) return toast.error(`Duplicate Discord username: ${m.discord_username}`);
        dh.add(k);
      }
    }

    setBusy(true);
    try {
      const payload = isTeamMode
        ? {
            team_name: teamName.trim(),
            logo_url: logoUrl,
            roster: roster.map((m) => ({
              ign: m.ign.trim(),
              player_id: m.player_id.trim(),
              discord_username: m.discord_username.trim(),
            })),
          }
        : {
            ign: captain.ign.trim(),
            player_id: captain.player_id.trim(),
            discord_username: captain.discord_username.trim(),
            logo_url: logoUrl,
          };
      const { data } = await api.post(`/tournaments/${tournament.id}/join`, payload);
      toast.success("Request sent — waiting for admin approval");
      onSubmitted && onSubmitted(data);
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        data-testid="join-tournament-modal"
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#0a0303] border border-red-500/30 rounded-2xl p-6 relative shadow-[0_0_60px_rgba(230,0,0,0.3)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <XIcon size={16} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          {isTeamMode ? <Users size={16} className="text-red-400" /> : <Zap size={16} className="text-red-400" />}
          <span className="text-[10px] uppercase tracking-widest text-red-400 font-mono">
            {isTeamMode ? `Team of ${teamSize} signup` : "Join request"}
          </span>
        </div>
        <h2 className="font-display font-bold text-2xl mb-1">{tournament.name}</h2>
        <p className="text-xs text-zinc-400 mb-5">
          {isTeamMode
            ? `Captain fills the roster for all ${teamSize} players. Admin will review and approve.`
            : "Admins review join requests. Your in-game username, player ID and Discord handle help them verify it's really you and reach out."}
        </p>

        {/* Team logo — required for every signup */}
        <div className="mb-4">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5 mb-2">
            <ImageIcon size={11} className="text-red-400" />
            {isTeamMode ? "Team Logo" : "Player Avatar / Logo"} *
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
              data-testid="join-logo-pick-btn"
              className={`relative h-20 w-20 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors ${
                logoUrl ? "border-red-500/50 bg-black" : "border-white/15 hover:border-red-500/40 bg-white/[0.02]"
              }`}
            >
              {logoUrl ? (
                <img
                  src={resolveAsset(logoUrl)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : uploadingLogo ? (
                <Loader2 size={20} className="animate-spin text-red-400" />
              ) : (
                <Upload size={22} className="text-zinc-500" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
                data-testid="join-logo-upload-btn"
              >
                {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {logoUrl ? "Change logo" : "Upload logo"}
              </button>
              <p className="text-[10px] font-mono text-zinc-500 mt-1">
                PNG / JPG · max 5 MB · square works best
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickLogo}
              className="hidden"
              data-testid="join-logo-file-input"
            />
          </div>
        </div>

        {isTeamMode && (
          <label className="block mb-4">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              Team Name
            </span>
            <input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="The Spicy Squad"
              maxLength={40}
              required
              autoFocus
              data-testid="join-team-name-input"
              className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-500"
            />
          </label>
        )}

        <div className="space-y-4">
          {roster.map((m, i) => (
            <div
              key={i}
              data-testid={`join-roster-slot-${i}`}
              className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? "bg-red-500 text-white" : "bg-white/10 text-zinc-300"}`}>
                  {i + 1}
                </div>
                <span className="text-[11px] uppercase tracking-widest font-mono text-zinc-400">
                  {i === 0 ? "You (captain)" : `Player ${i + 1}`}
                </span>
              </div>

              <div className="space-y-2.5">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">In-game Username</span>
                  <input
                    value={m.ign}
                    onChange={(e) => updateMember(i, "ign", e.target.value)}
                    placeholder="e.g. ShelowBlow"
                    maxLength={40}
                    required
                    autoFocus={!isTeamMode && i === 0}
                    data-testid={i === 0 ? "join-ign-input" : `join-ign-input-${i}`}
                    className="mt-1 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Player ID</span>
                  <input
                    value={m.player_id}
                    onChange={(e) => updateMember(i, "player_id", e.target.value)}
                    placeholder="In-game player ID"
                    maxLength={40}
                    required
                    data-testid={i === 0 ? "join-playerid-input" : `join-playerid-input-${i}`}
                    className="mt-1 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500 font-mono"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1">
                    <MessageSquare size={10} className="text-[#7e88ff]" /> Discord Username
                  </span>
                  <input
                    value={m.discord_username}
                    onChange={(e) => updateMember(i, "discord_username", e.target.value)}
                    placeholder="e.g. shelowblow#1234"
                    maxLength={60}
                    required
                    data-testid={i === 0 ? "join-discord-input" : `join-discord-input-${i}`}
                    className="mt-1 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500 font-mono"
                  />
                </label>
              </div>
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="join-submit-btn"
          className="btn-liquid w-full justify-center mt-5 !py-3 disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          {busy ? "Submitting..." : isTeamMode ? `Submit team of ${teamSize}` : "Send request"}
        </button>
      </form>
    </div>
  );
}
